// ============================================================================
// Roof Designer · DesignerCanvas — interaktives Canvas mit realem Kontext
// ----------------------------------------------------------------------------
// Zeichnet (in dieser Reihenfolge):
//   - Satelliten-Hintergrund (Esri-Tiles via BackgroundGeoRef, lagerichtig) ODER
//     hochgeladenes Dachbild (DataURL) ODER neutraler Raster-Hintergrund
//   - Kalibrierungspunkte + -linie
//   - Dach-Polygon (grün, mit Eckpunkten)
//   - Hindernisse (rot mit Setback-Zone)
//   - Modul-Belegung (blau, aus DesignerResult.pixelPlacements)
//   - Maus-Cursor + Werkzeug-Hinweis
// Ohne Hintergrund zeigt ein HTML-Overlay einen ehrlichen Call-to-Action —
// das abstrakte Raster gibt sich nie als finale Planungsbasis aus.
// Werkzeug-Events (Klick, Drag) werden als Reducer-Actions dispatcht.
// ============================================================================
import { useRef, useEffect, useCallback, useState } from "react";
import type { DesignerState, PixelPt, ObstacleDraft, PixelRect } from "../types.js";
import type { DesignerResult } from "../roof-engine.js";
import type { DesignerAction } from "../state.js";
import { computeTileLayout, fileToScaledDataUrl, ESRI_ATTRIBUTION } from "../background.js";

interface Props {
  state: DesignerState;
  result: DesignerResult;
  dispatch: (a: DesignerAction) => void;
}

// ─── Satelliten-Tile-Cache (modulweit, überlebt Re-Renders) ──────────────────
const tileCache = new Map<string, HTMLImageElement | "loading" | "error">();

// ─── Draw-Helfer ─────────────────────────────────────────────────────────────
function drawGrid(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.strokeStyle = "rgba(148,163,184,0.07)";
  ctx.lineWidth = 1;
  for (let x = 0; x < w; x += 40) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
  }
  for (let y = 0; y < h; y += 40) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
  }
  // dezente Hauptlinien alle 200px
  ctx.strokeStyle = "rgba(148,163,184,0.12)";
  for (let x = 0; x < w; x += 200) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
  }
  for (let y = 0; y < h; y += 200) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
  }
}

function drawCalib(ctx: CanvasRenderingContext2D, cal: DesignerState["calibration"]) {
  const col = cal.confirmed ? "#22d3ee" : "#fbbf24";
  if (cal.a) {
    ctx.beginPath(); ctx.arc(cal.a.x, cal.a.y, 6, 0, Math.PI * 2);
    ctx.fillStyle = col; ctx.fill();
  }
  if (cal.b) {
    ctx.beginPath(); ctx.arc(cal.b.x, cal.b.y, 6, 0, Math.PI * 2);
    ctx.fillStyle = col; ctx.fill();
  }
  if (cal.a && cal.b) {
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = col; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(cal.a.x, cal.a.y); ctx.lineTo(cal.b.x, cal.b.y); ctx.stroke();
    ctx.setLineDash([]);
    const mx = (cal.a.x + cal.b.x) / 2, my = (cal.a.y + cal.b.y) / 2;
    ctx.fillStyle = col; ctx.font = "11px monospace";
    ctx.fillText(`${cal.realLengthM} m`, mx + 6, my - 6);
  }
}

function drawPolygon(
  ctx: CanvasRenderingContext2D,
  polygon: PixelPt[],
  closed: boolean,
  hasRealBackground: boolean,
) {
  if (polygon.length === 0) return;
  // Auf realem Hintergrund: kräftigere Kontur + Außen-Abdunkelung der Füllung
  ctx.strokeStyle = "#22c55e";
  ctx.lineWidth = hasRealBackground ? 2.5 : 2;
  ctx.fillStyle = hasRealBackground ? "rgba(34,197,94,0.14)" : "rgba(34,197,94,0.08)";
  if (hasRealBackground) {
    ctx.shadowColor = "rgba(0,0,0,0.55)";
    ctx.shadowBlur = 6;
  }
  ctx.beginPath();
  ctx.moveTo(polygon[0]!.x, polygon[0]!.y);
  for (let i = 1; i < polygon.length; i++) {
    ctx.lineTo(polygon[i]!.x, polygon[i]!.y);
  }
  if (closed) { ctx.closePath(); ctx.fill(); }
  ctx.stroke();
  ctx.shadowBlur = 0;
  polygon.forEach((p, i) => {
    ctx.beginPath(); ctx.arc(p.x, p.y, i === 0 ? 7 : 4, 0, Math.PI * 2);
    ctx.fillStyle = i === 0 ? "#facc15" : "#22c55e"; ctx.fill();
    if (hasRealBackground) {
      ctx.strokeStyle = "rgba(0,0,0,0.5)"; ctx.lineWidth = 1; ctx.stroke();
    }
  });
}

function drawObstacles(ctx: CanvasRenderingContext2D, obstacles: ObstacleDraft[], setbackPx: number) {
  obstacles.forEach((obs) => {
    ctx.fillStyle = "rgba(239,68,68,0.2)";
    ctx.fillRect(obs.x, obs.y, obs.w, obs.h);
    ctx.strokeStyle = "#ef4444"; ctx.lineWidth = 2;
    ctx.strokeRect(obs.x, obs.y, obs.w, obs.h);
    if (setbackPx > 0) {
      ctx.strokeStyle = "rgba(239,68,68,0.4)"; ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(obs.x - setbackPx, obs.y - setbackPx, obs.w + 2 * setbackPx, obs.h + 2 * setbackPx);
      ctx.setLineDash([]);
    }
    ctx.fillStyle = "rgba(239,68,68,0.85)"; ctx.font = "11px monospace";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText("X", obs.x + obs.w / 2, obs.y + obs.h / 2);
    ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
  });
}

function drawModules(ctx: CanvasRenderingContext2D, placements: PixelRect[], hasRealBackground: boolean) {
  ctx.fillStyle = hasRealBackground ? "rgba(30,64,175,0.55)" : "rgba(37,99,235,0.32)";
  ctx.strokeStyle = hasRealBackground ? "#93c5fd" : "#3b82f6";
  ctx.lineWidth = 1;
  placements.forEach((p) => {
    ctx.fillRect(p.x, p.y, p.w, p.h);
    ctx.strokeRect(p.x, p.y, p.w, p.h);
  });
}

function drawObstacleDragPreview(ctx: CanvasRenderingContext2D, start: PixelPt, mouse: PixelPt) {
  const x = Math.min(start.x, mouse.x), y = Math.min(start.y, mouse.y);
  const w = Math.abs(mouse.x - start.x), h = Math.abs(mouse.y - start.y);
  ctx.fillStyle = "rgba(239,68,68,0.12)";
  ctx.fillRect(x, y, w, h);
  ctx.setLineDash([6, 4]);
  ctx.strokeStyle = "#ef4444"; ctx.lineWidth = 2;
  ctx.strokeRect(x, y, w, h);
  ctx.setLineDash([]);
}

function drawPolygonEdgePreview(ctx: CanvasRenderingContext2D, last: PixelPt, mouse: PixelPt) {
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = "rgba(34,197,94,0.5)"; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(mouse.x, mouse.y); ctx.stroke();
  ctx.setLineDash([]);
}

function drawCursor(ctx: CanvasRenderingContext2D, p: PixelPt) {
  ctx.strokeStyle = "rgba(255,255,255,0.35)"; ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(p.x - 10, p.y); ctx.lineTo(p.x + 10, p.y);
  ctx.moveTo(p.x, p.y - 10); ctx.lineTo(p.x, p.y + 10);
  ctx.stroke();
}

const GUIDE: Record<string, string> = {
  calibrate: "Klick 1 = Startpunkt · Klick 2 = Endpunkt · dann Länge eingeben und Setzen",
  polygon:   "Klicken = Punkt · Doppelklick oder erster Punkt = Polygon schliessen",
  obstacle:  "Klicken + Ziehen = Hindernis-Rechteck zeichnen",
  select:    "Klicken auf Hindernis = entfernen",
};

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
export function DesignerCanvas({ state, result, dispatch }: Props) {
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseRef   = useRef<PixelPt | null>(null);
  const dragRef    = useRef<PixelPt | null>(null);
  const bgImgRef   = useRef<HTMLImageElement | null>(null);
  const [bgImageFailed, setBgImageFailed] = useState(false);

  const satelliteActive = state.backgroundGeoRef != null && state.backgroundGeoRef.enabled;

  // Hintergrundbild laden (DataURL; alte blob:-URLs schlagen nach Reload fehl
  // und werden als "kein Bild" behandelt — Overlay zeigt dann den CTA).
  useEffect(() => {
    setBgImageFailed(false);
    if (!state.backgroundImage) { bgImgRef.current = null; return; }
    const img = new Image();
    img.onload = () => { bgImgRef.current = img; redrawRef.current(); };
    img.onerror = () => { bgImgRef.current = null; setBgImageFailed(true); redrawRef.current(); };
    img.src = state.backgroundImage;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.backgroundImage]);

  const hasUploadedImage = state.backgroundImage != null && !bgImageFailed;
  const hasRealBackground = hasUploadedImage || satelliteActive;

  // Zeichnen
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const { width: w, height: h } = canvas;

    // Grundfläche
    ctx.fillStyle = "#0b1220";
    ctx.fillRect(0, 0, w, h);

    let satelliteDrawn = false;

    // 1) Satelliten-Hintergrund (lagerichtig zur importierten Geometrie)
    if (satelliteActive && state.backgroundGeoRef) {
      const layout = computeTileLayout(state.backgroundGeoRef, w, h);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      for (const t of layout.tiles) {
        const key = t.url;
        const cached = tileCache.get(key);
        if (cached instanceof HTMLImageElement) {
          ctx.drawImage(cached, t.dx, t.dy, t.dSize + 0.5, t.dSize + 0.5);
          satelliteDrawn = true;
        } else if (cached === undefined) {
          tileCache.set(key, "loading");
          const img = new Image();
          img.onload = () => { tileCache.set(key, img); redrawRef.current(); };
          img.onerror = () => { tileCache.set(key, "error"); };
          img.src = t.url;
        }
      }
      if (satelliteDrawn) {
        // leichte Abdunkelung, damit Overlays (Polygon/Module) klar lesbar bleiben
        ctx.fillStyle = "rgba(2,6,23,0.18)";
        ctx.fillRect(0, 0, w, h);
      }
    }

    // 2) Hochgeladenes Dachbild (liegt ÜBER dem Satellit, falls beide vorhanden)
    const bg = bgImgRef.current;
    if (hasUploadedImage && bg) {
      const scale = Math.min(w / bg.width, h / bg.height);
      const dx = (w - bg.width * scale) / 2, dy = (h - bg.height * scale) / 2;
      ctx.globalAlpha = satelliteDrawn ? 0.85 : 0.9;
      ctx.drawImage(bg, dx, dy, bg.width * scale, bg.height * scale);
      ctx.globalAlpha = 1;
    }

    // 3) Raster nur ohne realen Hintergrund (ehrlich: abstrakte Arbeitsfläche)
    if (!satelliteDrawn && !(hasUploadedImage && bg)) {
      drawGrid(ctx, w, h);
    }

    const mPerPx = state.calibration.metersPerPixel ?? 1;
    const obsPx = state.obstacleSetbackM / mPerPx;

    drawCalib(ctx, state.calibration);
    drawObstacles(ctx, state.obstacles, obsPx);
    drawPolygon(ctx, state.polygon, state.polygonClosed, hasRealBackground);
    if (result.status === "ok") drawModules(ctx, result.pixelPlacements, hasRealBackground);

    const mouse = mouseRef.current;

    // Drag-Vorschau Hindernis
    if (state.activeTool === "obstacle" && dragRef.current && mouse) {
      drawObstacleDragPreview(ctx, dragRef.current, mouse);
    }

    // Kante-Vorschau beim Zeichnen
    if (state.activeTool === "polygon" && !state.polygonClosed && state.polygon.length > 0 && mouse) {
      drawPolygonEdgePreview(ctx, state.polygon[state.polygon.length - 1]!, mouse);
    }

    if (mouse) drawCursor(ctx, mouse);

    // Anleitung (links unten)
    ctx.fillStyle = "rgba(255,255,255,0.45)";
    ctx.font = "11px monospace";
    ctx.fillText(GUIDE[state.activeTool] ?? "", 10, h - 10);

    // Pflicht-Attribution bei Satelliten-Tiles (rechts unten)
    if (satelliteDrawn) {
      ctx.font = "10px sans-serif";
      const label = ESRI_ATTRIBUTION;
      const tw = ctx.measureText(label).width;
      ctx.fillStyle = "rgba(2,6,23,0.55)";
      ctx.fillRect(w - tw - 14, h - 20, tw + 14, 20);
      ctx.fillStyle = "rgba(255,255,255,0.75)";
      ctx.fillText(label, w - tw - 7, h - 7);
    }
  }, [state, result, satelliteActive, hasUploadedImage, hasRealBackground]);

  // Ref damit ResizeObserver immer aktuelle Version nutzt
  const redrawRef = useRef(redraw);
  useEffect(() => { redrawRef.current = redraw; });

  // ResizeObserver — Canvas-Größe anpassen
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const obs = new ResizeObserver(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width  = container.clientWidth;
      canvas.height = container.clientHeight;
      redrawRef.current();
    });
    obs.observe(container);
    return () => obs.disconnect();
  }, []);

  useEffect(() => { redraw(); }, [redraw]);

  // ─── Bild-Upload (aus dem CTA-Overlay) ──────────────────────────────────────
  const handleImageFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const dataUrl = await fileToScaledDataUrl(file);
      dispatch({ type: "SET_BACKGROUND_IMAGE", url: dataUrl });
    } catch {
      // Lesefehler → Zustand unverändert lassen
    }
  };

  // ─── Event-Helpers ──────────────────────────────────────────────────────────
  const getPos = (e: React.MouseEvent<HTMLCanvasElement>): PixelPt => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const p = getPos(e);
    switch (state.activeTool) {
      case "polygon":
        if (state.polygonClosed) return;
        if (state.polygon.length >= 3) {
          const first = state.polygon[0]!;
          if (Math.hypot(p.x - first.x, p.y - first.y) < 15) {
            dispatch({ type: "CLOSE_POLYGON" });
            return;
          }
        }
        dispatch({ type: "ADD_POLYGON_POINT", point: p });
        break;
      case "calibrate":
        dispatch({ type: "SET_CALIB_POINT", point: p });
        break;
      case "select": {
        const hit = state.obstacles.find(
          (o) => p.x >= o.x && p.x <= o.x + o.w && p.y >= o.y && p.y <= o.y + o.h,
        );
        if (hit) dispatch({ type: "REMOVE_OBSTACLE", id: hit.id });
        break;
      }
    }
  };

  const handleDblClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (state.activeTool === "polygon" && !state.polygonClosed && state.polygon.length >= 3) {
      e.preventDefault();
      dispatch({ type: "CLOSE_POLYGON" });
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (state.activeTool === "obstacle") {
      const p = getPos(e);
      dragRef.current = p;
      dispatch({ type: "SET_OBSTACLE_START", point: p });
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (state.activeTool === "obstacle" && dragRef.current) {
      const p = getPos(e);
      const s = dragRef.current;
      const x = Math.min(s.x, p.x), y = Math.min(s.y, p.y);
      const w = Math.abs(p.x - s.x), h = Math.abs(p.y - s.y);
      if (w > 8 && h > 8) {
        dispatch({ type: "ADD_OBSTACLE", obstacle: { type: state.obstacleDraftType, x, y, w, h } });
      }
      dragRef.current = null;
      dispatch({ type: "SET_OBSTACLE_START", point: null });
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    mouseRef.current = getPos(e);
    redraw();
  };

  const handleMouseLeave = () => {
    mouseRef.current = null;
    redraw();
  };

  const cursors: Record<DesignerState["activeTool"], string> = {
    calibrate: "cell",
    polygon:   "crosshair",
    obstacle:  "crosshair",
    select:    "default",
  };

  const hasDrawing = state.polygon.length > 0;
  // Satellit verfügbar, nur ausgeblendet?
  const satelliteAvailable = state.backgroundGeoRef != null && !state.backgroundGeoRef.enabled;

  return (
    <div
      ref={containerRef}
      className="rd-canvas-wrap"
      style={{ flex: 1, minHeight: 0, position: "relative", overflow: "hidden", userSelect: "none" }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: "block", cursor: cursors[state.activeTool] }}
        onClick={handleClick}
        onDoubleClick={handleDblClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />

      {/* ── Ehrlicher Hinweis: keine reale Planungsbasis vorhanden ── */}
      {!hasRealBackground && (
        hasDrawing ? (
          // Schlanke Leiste, blockiert das Arbeiten nicht
          <div className="rd-cta rd-cta--banner" data-testid="canvas-missing-bg-banner">
            <span>
              ⚠ Ohne Dachbild / Satellit ist dies eine abstrakte Skizze — keine reale Planungsbasis.
            </span>
            <label className="rd-cta__btn">
              Dachbild laden
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => { void handleImageFile(e.target.files?.[0]); e.target.value = ""; }}
              />
            </label>
            {satelliteAvailable && (
              <button
                className="rd-cta__btn"
                onClick={() => dispatch({ type: "SET_SATELLITE_ENABLED", enabled: true })}
              >
                Satellit einblenden
              </button>
            )}
          </div>
        ) : (
          // Zentrale Karte bei leerem Canvas
          <div className="rd-cta rd-cta--card" data-testid="canvas-missing-bg-card">
            <div className="rd-cta__title">Reale Planungsbasis laden</div>
            <p className="rd-cta__text">
              Damit das Dach im Kontext des Gebäudes geplant wird:
              <strong> Dachbild oder Satelliten-Ausschnitt laden.</strong>
            </p>
            <div className="rd-cta__actions">
              <label className="rd-cta__btn rd-cta__btn--primary">
                📷 Dachbild laden
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => { void handleImageFile(e.target.files?.[0]); e.target.value = ""; }}
                />
              </label>
              {satelliteAvailable && (
                <button
                  className="rd-cta__btn rd-cta__btn--primary"
                  onClick={() => dispatch({ type: "SET_SATELLITE_ENABLED", enabled: true })}
                >
                  🛰 Satellit einblenden
                </button>
              )}
            </div>
            <p className="rd-cta__hint">
              🛰 Satelliten-Hintergrund erscheint automatisch, wenn oben eine
              Gebäude-Dachfläche mit Geometrie übernommen wird. Alternativ ohne
              Hintergrund skizzieren (abstrakte Arbeitsfläche, ENTWURF).
            </p>
          </div>
        )
      )}
    </div>
  );
}
